import Base from './Base'

export type CelebrationConfig = InstanceType<typeof Celebration>['initConfig']
export type PartialCelebrationConfig = Partial<CelebrationConfig>

export default class Celebration extends Base {
  declare ['constructor']: typeof Celebration

  static helpers = {
    ...super.helpers,

    getFinalVelocity: ({ vi, a, d }: Record<'vi' | 'a' | 'd', number>) => {
      return Math.sqrt(vi ** 2 + 2 * a * d)
    },

    getTime: ({ vi, a, d }: Record<'vi' | 'a' | 'd', number>) => {
      const v = this.helpers.getFinalVelocity({ vi, a, d })

      return (v - vi) / a
    },

    getDistance: ({ vi, t, a }: Record<'vi' | 'a' | 't', number>) => {
      return vi * t + 0.5 * a * t ** 2
    },

    // debounce: (func: () => void, timeout: number) => {
    //   let timer: ReturnType<typeof setTimeout>

    //   return (...args: never) => {
    //     clearTimeout(timer)
    //     timer = setTimeout(() => {
    //       func.apply(null, args)
    //     }, timeout)
    //   }
    // },

    // throttle: (func: () => void, delay: number) => {
    //   let timerId: ReturnType<typeof setTimeout> = null

    //   return (...args: never) => {
    //     if (timerId === null) {
    //       func.apply(null, args)
    //       timerId = setTimeout(() => {
    //         timerId = null
    //       }, delay)
    //     }
    //   }
    // },
  }

  srcSet: string[] = []

  initConfig = {
    num: 0,
    /** items per 1000px */
    density: 60,
    /** [px] distribution of sizes of items */
    sizes: [30, 35, 45, 50],
    /** initial velocity, [px/s] */
    velocity: 80,
    /** distribution of velocity [1, velocityMaxRatio] * velocity */
    velocityMaxRatio: 5,
    /** [px/s^2] */
    acceleration: 15,
    /** distribution of velocity [1, accelerationMaxRatio] * velocity */
    accelerationMaxRatio: 2,
    /** [rad], the angle of fluctuation */
    frequency: 0.01,
    /** [px], the max deviation from the linear move */
    amplitude: 20,
    rotationSpeed: 10,
    animationDuration: undefined as any,
  }

  private items: ReturnType<typeof this.prepareImages> = []
  private config: CelebrationConfig
  private animation: ReturnType<typeof this.constructor.helpers.createAnimation>
  private initTimeStamp: number

  private drawImage({
    params,
    rotationAngle,
  }: {
    params: [HTMLImageElement, number, number, number, number]
    rotationAngle: number
  }) {
    const [img, x, y, w, h] = params
    const sx = w / 2
    const sy = h / 2
    const cr = Math.cos(rotationAngle)
    const sr = Math.sin(rotationAngle)

    // perform rotation and translation of image
    this.ctx.setTransform(cr, sr, -sr, cr, x - (cr * sx - sr * sy), y - (sr * sx + cr * sy))
    this.ctx.drawImage(img, 0, 0, w, h)
    this.ctx.resetTransform()
  }

  private drawImages(timeDiff: number) {
    const { getDistance, toRad } = this.constructor.helpers
    const rotationSpeed = this.config.rotationSpeed

    this.items.forEach(
      ({ img, getDimensions, velocity, acceleration, xPos, yPos, amplitude, frequency, trackTime, delay }, index) => {
        if (timeDiff < delay || img.width === 0) {
          return
        }

        const { imgW, imgH } = getDimensions()
        const rotationAngle = toRad((timeDiff * rotationSpeed) / imgW)
        const dt = (timeDiff - delay) % trackTime
        const dy = yPos + getDistance({ vi: velocity, a: acceleration, t: dt })
        const pathFn = index % 2 ? Math.sin : Math.cos
        const dx = xPos + pathFn(dy * frequency) * amplitude

        this.drawImage({
          params: [img, dx, dy, imgW, imgH],
          rotationAngle,
        })
      },
    )
  }

  private draw(timeDiff = 0) {
    this.ctx.clearRect(0, 0, this.canvasParams.w, this.canvasParams.h)

    this.drawImages(timeDiff)
  }

  private prepareImages() {
    const { getTime, getRandomFloatInclusive, getRandomIntInclusive } = this.constructor.helpers
    const { num, sizes, velocity, velocityMaxRatio, acceleration, accelerationMaxRatio, amplitude, frequency } =
      this.config

    const images = this.srcSet.map((src) => {
      const img = new Image()
      img.src = src

      return img
    })

    const minTrackTime = getTime({
      vi: velocity * velocityMaxRatio,
      a: acceleration * accelerationMaxRatio,
      d: this.canvasParams.h + Math.min(...sizes),
    })

    const items = [...Array(num)].map(() => {
      const vi = velocity * getRandomFloatInclusive(1, velocityMaxRatio)
      const a = acceleration * getRandomFloatInclusive(1, accelerationMaxRatio)
      const img = images[getRandomIntInclusive(0, images.length - 1)]
      const imgSize = sizes[getRandomIntInclusive(0, sizes.length - 1)]

      const getDimensions = () => {
        const naturalWidth = img.naturalWidth || img.width
        const naturalHeight = img.naturalHeight || img.height
        const aspectRatio = naturalWidth && naturalHeight ? naturalWidth / naturalHeight : 1

        return {
          imgW: imgSize,
          imgH: imgSize / aspectRatio,
        }
      }

      return {
        img,
        getDimensions,
        velocity: vi,
        acceleration: a,
        xPos: Math.random() * (this.canvasParams.w + imgSize),
        yPos: -imgSize,
        amplitude: amplitude,
        frequency: frequency,
        trackTime: getTime({
          vi,
          a,
          d: this.canvasParams.h + imgSize,
        }),
        delay: Math.random() * minTrackTime,
      }
    })

    this.items = items

    return items
  }

  private prepareParams() {
    this.config = { ...this.initConfig }

    const { num, density, sizes, velocity, acceleration, frequency, amplitude } = this.config
    const dpr = this.canvasParams.dpr

    this.config.num = num || Math.round(((density / 1000) * this.canvasParams.w) / dpr)
    this.config.sizes = sizes.map((size) => size * dpr)
    this.config.velocity = (velocity * dpr) / 1000
    this.config.acceleration = (acceleration * dpr) / 1000 ** 2
    this.config.frequency = frequency / dpr
    this.config.amplitude = amplitude * dpr
  }

  private createAnimation(timer: number) {
    const { createAnimation } = this.constructor.helpers

    this.animation = createAnimation({
      draw: ({ timeDiff }) => {
        this.draw(timeDiff)
      },
      timer,
      onEnd: () => {
        this.ctx.clearRect(0, 0, this.canvasParams.w, this.canvasParams.h)
      },
    })
  }

  private wireEvents() {
    const orientationMM = window.matchMedia('(orientation: portrait)')

    const orientationChangeListener = () => {
      const duration = this.initConfig.animationDuration
      const timePassed = Date.now() - this.initTimeStamp
      if (!duration) {
        this.replay()

        return
      }
      if (timePassed < duration - 2000) {
        this.replay(duration - timePassed)
      } else {
        this.animation.stop()
        orientationMM.removeEventListener('change', orientationChangeListener)
      }
    }

    orientationMM.addEventListener('change', orientationChangeListener)

    return () => {
      orientationMM.removeEventListener('change', orientationChangeListener)
    }
  }

  replay(_duration?: number) {
    const { availWidth, availHeight } = window.screen
    const duration = _duration || this.initConfig.animationDuration
    const portrait = window.matchMedia('(orientation: portrait)').matches

    const [width, height] = [availWidth, availHeight].sort((a, b) => (portrait ? a - b : b - a))

    this.animation?.stop()

    super.setCanvasParams(this.node, { width, height })
    this.node.style.width = `${width}px`
    this.node.style.height = `${height}px`

    this.prepareParams()
    this.prepareImages()

    this.createAnimation(duration)

    this.animation.play()
  }

  init(node: HTMLCanvasElement, { srcSet, params }: { srcSet: string[]; params?: PartialCelebrationConfig }) {
    this.node = node

    this.srcSet = srcSet

    this.initConfig = { ...this.initConfig, ...params }

    if (!node.id) {
      // the id needs for checking of the node presence
      // to perform the clean up when it is dismounted
      node.setAttribute('id', 'wow-celebration-canvas')
    }

    const resetEvents = this.wireEvents()

    super.setCleanUpObserver(() => {
      this.animation.stop()
      resetEvents()
    })

    this.replay()

    this.initTimeStamp = Date.now()
  }
}
